import { firstValueFrom, Observable } from 'rxjs';
import { Secret } from '../../../interfaces/secrets.interface';

/**
 * Shared implementations of EHQ.sendNotification (SCRUM-540) and
 * EHQ.getSecretValue (SCRUM-584) used by the Python step bridge
 * (IntrospectionBridgeService) and the JS step bridge (JavascriptService).
 *
 * Pulled out as pure functions so both runtimes share one source of truth —
 * before extraction the JS bridge maintained an inline copy that drifted from
 * the Python implementation and tripped SonarCloud duplication on SCRUM-673.
 */

export interface AppStateLike {
  projectId?: () => number | undefined;
  project?: () => { projectId?: number } | undefined | null;
}

export interface PushDestinationSendable {
  send(
    projectId: number,
    request: {
      destination: string;
      message: string;
      options?: Record<string, unknown>;
    },
  ): Observable<unknown>;
}

export interface SecretsValueFetchable {
  getSecrets?: (projectId: number) => Observable<Secret[]>;
  getSecretValue?: (
    projectId: number,
    secretId: number,
  ) => Observable<{ value?: string }>;
}

function resolveProjectId(appStateService: AppStateLike): number | undefined {
  return (
    appStateService.projectId?.() ??
    appStateService.project?.()?.projectId ??
    undefined
  );
}

/**
 * Validates args, resolves the active project, and posts the notification.
 * Returns true on success so step code receives a truthy value through the
 * async import bridge.
 */
export async function sendNotificationViaBridge(
  args: unknown[],
  appStateService: AppStateLike,
  pushDestinationService: PushDestinationSendable | undefined,
): Promise<boolean> {
  const destination = args?.[0];
  const message = args?.[1];
  const options = args?.[2];

  if (!destination || typeof destination !== 'string') {
    throw new Error('sendNotification requires a destination name');
  }
  if (!message || typeof message !== 'string') {
    throw new Error('sendNotification requires a message');
  }

  const projectId = resolveProjectId(appStateService);
  if (!projectId) {
    throw new Error('sendNotification: no active project');
  }
  if (!pushDestinationService?.send) {
    throw new Error('sendNotification: push destination service unavailable');
  }

  await firstValueFrom(
    pushDestinationService.send(projectId, {
      destination,
      message,
      options: options as Record<string, unknown> | undefined,
    }),
  );
  return true;
}

/**
 * Resolves nameOrId → secretId, fetches the value, and surfaces a meaningful
 * error when the value is null/undefined (the async bridge would otherwise
 * coerce a void return into true).
 *
 * `lookupByName` is consulted first when the caller passes a string; the
 * Python bridge supplies a sync cache lookup, the JS bridge omits it so the
 * helper falls back to a fresh getSecrets() fetch. Numeric ids skip lookup
 * entirely.
 */
export async function getSecretValueViaBridge(
  args: unknown[],
  appStateService: AppStateLike,
  secretsService: SecretsValueFetchable | undefined,
  lookupByName?: (name: string) => Secret | undefined,
): Promise<string> {
  const nameOrId = args?.[0];
  if (nameOrId === undefined || nameOrId === null) {
    throw new Error('getSecretValue requires a secret name or id');
  }

  const projectId = resolveProjectId(appStateService);
  if (!projectId) {
    throw new Error('getSecretValue: no active project');
  }
  if (!secretsService?.getSecretValue) {
    throw new Error('getSecretValue: secrets service unavailable');
  }

  const secretId =
    typeof nameOrId === 'number'
      ? nameOrId
      : await resolveSecretIdByName(
          nameOrId as string,
          projectId,
          secretsService,
          lookupByName,
        );

  const response = await firstValueFrom(
    secretsService.getSecretValue(projectId, secretId),
  );
  if (response?.value === undefined || response?.value === null) {
    throw new Error(
      `getSecretValue: no value returned for secret ${secretId} ` +
      `(it may not be gettable)`,
    );
  }
  return response.value;
}

async function resolveSecretIdByName(
  name: string,
  projectId: number,
  secretsService: SecretsValueFetchable,
  lookupByName: ((name: string) => Secret | undefined) | undefined,
): Promise<number> {
  const cached = lookupByName?.(name);
  if (cached) {
    return cached.secretId;
  }
  if (!secretsService.getSecrets) {
    throw new Error(`getSecretValue: secret "${name}" not found`);
  }
  const secrets = await firstValueFrom(secretsService.getSecrets(projectId));
  const match = (secrets ?? []).find(s => s.name === name);
  if (!match) {
    throw new Error(`getSecretValue: secret "${name}" not found`);
  }
  return match.secretId;
}
